import {sendEvent} from '@github-ui/hydro-analytics'
import {announce} from '@github-ui/aria-live'
import {FormControl, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {type FormEvent, type RefObject, useCallback, useId, useRef, useState} from 'react'
import {setTaskTitleOverride} from '@github-ui/copilot-shared-ui/hooks/use-task-title-override'
import {useRenameTask} from '../hooks/use-rename-task'

interface RenameTaskDialogProps {
  title?: string
  currentName: string
  taskId?: string
  repoOwner?: string
  repoName?: string
  onClose: () => void
  returnFocusRef: RefObject<HTMLElement | null>
  apiUrl?: string
  onRenamed?: (taskId: string) => void
}

export function RenameTaskDialog({
  title = 'Rename task',
  currentName,
  taskId,
  repoOwner,
  repoName,
  onClose,
  returnFocusRef,
  apiUrl,
  onRenamed,
}: RenameTaskDialogProps) {
  const [invalid, setInvalid] = useState<'required' | 'max_length' | 'request' | undefined>(undefined)
  const inputRef = useRef<HTMLInputElement>(null)
  const formId = useId()
  const dialogId = useId()
  const errorId = useId()
  const {mutateAsync: renameMutateAsync, isPending: isRenamePending} = useRenameTask({
    owner: repoOwner,
    repo: repoName,
    taskId,
    apiUrl,
  })

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      const value = inputRef.current?.value?.trim() ?? ''

      if (!value) {
        setInvalid('required')
        inputRef.current?.focus()
      } else if (value.length > 100) {
        setInvalid('max_length')
        inputRef.current?.focus()
      } else {
        setInvalid(undefined)
        try {
          await renameMutateAsync({name: value})
          if (taskId) {
            setTaskTitleOverride(taskId, value, currentName)
            onRenamed?.(taskId)
          }
          sendEvent('agent_sessions.task_actions', {
            target: 'RENAME_TASK_SAVE',
            task_id: taskId,
          })
          onClose()
        } catch {
          setInvalid('request')
          announce('Failed to rename. Please try again.', {assertive: true})
        }
      }
    },
    [renameMutateAsync, taskId, currentName, onClose, onRenamed],
  )

  const handleClose = useCallback(() => {
    sendEvent('agent_sessions.task_actions', {
      target: 'RENAME_TASK_CANCEL',
      task_id: taskId,
    })
    onClose()
  }, [taskId, onClose])

  return (
    <Dialog
      title={<span id={dialogId}>{title}</span>}
      onClose={handleClose}
      width="large"
      footerButtons={[
        {
          content: 'Cancel',
          onClick: handleClose,
        },
        {
          content: 'Update',
          buttonType: 'primary',
          type: 'submit',
          form: formId,
          disabled: isRenamePending,
        },
      ]}
      initialFocusRef={inputRef}
      returnFocusRef={returnFocusRef}
    >
      <form id={formId} onSubmit={submit}>
        <FormControl required disabled={isRenamePending}>
          <FormControl.Label visuallyHidden>{title}</FormControl.Label>
          <TextInput
            aria-labelledby={dialogId}
            aria-describedby={invalid ? errorId : undefined}
            aria-invalid={invalid ? 'true' : undefined}
            ref={inputRef}
            defaultValue={currentName}
            block
          />
          {invalid && (
            <FormControl.Validation id={errorId} variant="error">
              {invalid === 'required'
                ? 'Enter a name'
                : invalid === 'max_length'
                  ? 'Name cannot exceed 100 characters'
                  : 'Failed to rename. Please try again.'}
            </FormControl.Validation>
          )}
        </FormControl>
      </form>
    </Dialog>
  )
}
